/**
 * useKeyboardAwareViewport
 *
 * The standard "real chat app" keyboard behavior on mobile (works the
 * same way on Android Chrome and iOS Safari):
 *
 *   1. The page is locked to the visible viewport (above the keyboard)
 *      via `body.chat-keyboard-aware { position: fixed; height:
 *      var(--chat-vvh) }` so iOS Safari can't auto-scroll the document
 *      and push the chat header off-screen when an input is focused.
 *
 *   2. `--chat-vvh` is published from `window.visualViewport.height` and
 *      tracks the visible area in real time. CSS that sets the chat
 *      surface height to `var(--chat-vvh) - <fixed offset>` shrinks
 *      smoothly as the keyboard slides in.
 *
 *   3. **The messages container's scrollTop is bumped by the same delta
 *      the viewport shrinks** on every visualViewport resize. Result:
 *      the bottom of the visible content stays glued to the new visible
 *      bottom edge, so the latest message stays just above the keyboard
 *      and the messages "scroll up" by exactly the keyboard's height —
 *      same as WhatsApp / iMessage / Telegram. When the keyboard closes,
 *      the delta is negative and the scroll naturally reverses.
 *
 *   4. **iOS-only counter-scroll** — iOS Safari auto-scrolls the layout
 *      viewport upward when an input is focused, even with the body
 *      lock. Without intervention this pushes the entire fixed-positioned
 *      body off-screen and the user sees a blank area at the bottom.
 *      We call `window.scrollTo(0, 0)` on every visualViewport tick AND
 *      on focusin/focusout to neutralize this. Gated by `isIOS()` so
 *      Android Chrome (which animates the keyboard correctly without
 *      this hack) isn't affected.
 */

import { useEffect } from 'react';

const BODY_CLASS = 'chat-keyboard-aware';

const isIOS = (): boolean =>
  typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(window.navigator.userAgent);

export function useKeyboardAwareViewport() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    document.body.classList.add(BODY_CLASS);

    const vv = window.visualViewport;
    let raf = 0;
    let prevHeight = vv?.height ?? window.innerHeight;

    // Initialise the CSS vars synchronously so the body lock has the
    // right size on first paint (no flash of full-height before the
    // first resize event fires).
    root.style.setProperty('--chat-vvh', `${prevHeight}px`);
    root.style.setProperty('--chat-vvtop', '0px');
    root.style.setProperty('--keyboard-height', '0px');

    const ios = isIOS();

    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = vv?.height ?? window.innerHeight;
        if (!Number.isFinite(h) || h <= 0) return;
        const top = vv?.offsetTop ?? 0;
        const kbHeight = Math.max(0, window.innerHeight - h);
        const diff = prevHeight - h; // positive: keyboard opening

        // Capture "was near the bottom?" state for each messages
        // container BEFORE we mutate --chat-vvh. The CSS var change
        // triggers a reflow that updates clientHeight, so reading
        // scrollHeight/scrollTop afterwards would mix old and new
        // measurements. We snapshot first.
        const targets = Array.from(
          document.querySelectorAll<HTMLElement>('.chat-messages-container')
        );
        const wasNearBottom = targets.map((el) => {
          // "Near bottom" = within one keyboard's worth of pixels (or 80px,
          // whichever is larger). On a fresh chat, the user is typically
          // pinned to the bottom; if they've scrolled up to read history,
          // we leave them where they are.
          const tolerance = Math.max(80, Math.abs(diff));
          return el.scrollHeight - el.scrollTop - el.clientHeight <= tolerance;
        });

        root.style.setProperty('--chat-vvh', `${h}px`);
        root.style.setProperty('--chat-vvtop', `${top}px`);
        root.style.setProperty('--keyboard-height', `${kbHeight}px`);

        // iOS auto-scrolls the layout viewport upward when an input is
        // focused or while the keyboard is animating. Even with body
        // `position: fixed; inset: 0`, iOS shifts the visual rendering
        // so the user sees the page move up and a blank area below.
        // Counter-scroll on every visualViewport tick to keep the
        // layout viewport pinned at scroll position 0. Gated to iOS so
        // Android Chrome (which doesn't have this auto-scroll quirk)
        // isn't fought.
        if (ios && (window.scrollX !== 0 || window.scrollY !== 0)) {
          window.scrollTo(0, 0);
        }

        if (Math.abs(diff) > 1 && targets.length) {
          targets.forEach((el, i) => {
            // Force synchronous layout so clientHeight reflects the new
            // --chat-vvh BEFORE we set scrollTop. Without this, browsers
            // can read stale clientHeight, miscompute the max scrollTop,
            // and apply our delta only partially.
            void el.offsetHeight;
            if (wasNearBottom[i]) {
              // Snap to bottom — same behavior as WhatsApp / ChatGPT
              // when you tap the input. The browser auto-clamps
              // scrollHeight to the new max so we don't have to compute
              // it ourselves.
              el.scrollTop = el.scrollHeight;
            } else {
              // User was reading history above the bottom. Preserve
              // their relative position by bumping scrollTop by exactly
              // the viewport delta.
              el.scrollTop += diff;
            }
          });
        }
        prevHeight = h;
      });
    };

    apply();
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', apply);

    // iOS-only: synchronous scrollTo(0, 0) on focusin and focusout.
    // The visualViewport listener above runs RAF-deferred, but the
    // focus event fires BEFORE iOS's auto-scroll-to-input — calling
    // scrollTo(0, 0) inside the focus handler beats iOS to it. Without
    // this the page visibly jumps even with the body lock.
    let onFocusIn: ((e: FocusEvent) => void) | null = null;
    let onFocusOut: ((e: FocusEvent) => void) | null = null;
    if (ios) {
      onFocusIn = () => {
        window.scrollTo(0, 0);
        // Fire apply() so the height var updates immediately too.
        apply();
      };
      onFocusOut = () => {
        window.scrollTo(0, 0);
        apply();
      };
      window.addEventListener('focusin', onFocusIn);
      window.addEventListener('focusout', onFocusOut);
    }

    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', apply);
      if (onFocusIn) window.removeEventListener('focusin', onFocusIn);
      if (onFocusOut) window.removeEventListener('focusout', onFocusOut);
      document.body.classList.remove(BODY_CLASS);
      root.style.removeProperty('--chat-vvh');
      root.style.removeProperty('--chat-vvtop');
      root.style.removeProperty('--keyboard-height');
    };
  }, []);
}
